import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserPlus, Search, Edit2, Phone, MapPin, Trash2, Upload, AlertCircle } from 'lucide-react';
import { OwnerLayout } from '../../../components/OwnerLayout';
import { PortfolioTabs } from '../../../components/PortfolioTabs';
import api from '../../../api';

function Toast({ msg, show }) {
  return <div className={`au-toast ${show ? 'show' : ''}`} style={{ bottom: '24px' }}>{msg}</div>;
}

const EMPTY = { clientId: '', fullName: '', primaryPhone: '', alternatePhone: '', address: '', branchId: '' };

export default function RecoveryCustomers() {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [branches, setBranches] = useState([]);
  const [branchFilter, setBranchFilter] = useState('');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [toast, setToast] = useState({ show: false, msg: '' });

  const showToast = (msg) => {
    setToast({ show: true, msg });
    setTimeout(() => setToast({ show: false, msg: '' }), 2500);
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (branchFilter) params.branchId = branchFilter;
      if (search.trim()) params.search = search.trim();
      const [custRes, brRes] = await Promise.all([
        api.get('/recovery/customers', { params }),
        api.get('/branches'),
      ]);
      setCustomers(custRes.data || []);
      setBranches(brRes.data || []);
    } catch (err) {
      console.error(err);
      showToast('Failed to load customers');
    } finally {
      setLoading(false);
    }
  }, [branchFilter, search]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setEditing(null);
    setForm({ ...EMPTY, branchId: branchFilter || (branches[0]?.id || '') });
    setShowModal(true);
  };

  const openEdit = (c) => {
    setEditing(c);
    setForm({
      clientId: c.clientId || '',
      fullName: c.fullName || '',
      primaryPhone: c.primaryPhone || '',
      alternatePhone: c.alternatePhone || '',
      address: c.address || '',
      branchId: c.branchId || '',
    });
    setShowModal(true);
  };

  const save = async () => {
    if (!form.clientId.trim() || !form.fullName.trim() || !form.primaryPhone.trim() || !form.branchId) {
      showToast('Client ID, name, phone and branch are required');
      return;
    }
    try {
      if (editing) {
        await api.patch(`/recovery/customers/${encodeURIComponent(editing.clientId)}`, form);
        showToast('Customer updated');
      } else {
        await api.post('/recovery/customers', form);
        showToast('Customer created');
      }
      setShowModal(false);
      load();
    } catch (err) {
      showToast(err.response?.data?.detail || 'Save failed');
    }
  };

  const removeCustomer = async (c) => {
    if (!window.confirm(`Delete customer "${c.fullName}" (${c.clientId})?\n\nThis will permanently remove the customer AND all associated loans, recovery cases, actions, promises and payments.`)) return;
    try {
      const res = await api.delete(`/recovery/customers/${encodeURIComponent(c.clientId)}`);
      showToast(`Customer deleted${res.data?.loansDeleted ? ` (${res.data.loansDeleted} loan(s))` : ''}`);
      load();
    } catch (err) {
      showToast(err.response?.data?.detail || 'Delete failed');
    }
  };

  const uploadStatement = async (c, file) => {
    if (!file) return;
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.post(`/recovery/customers/${encodeURIComponent(c.clientId)}/statements/upload`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      showToast(`Statement applied to ${res.data?.loanAccountNumber || 'loan'}`);
      load();
    } catch (err) {
      showToast(err.response?.data?.detail || 'Statement upload failed');
    }
  };

  return (
    <OwnerLayout title="Recovery · Customers">
      <PortfolioTabs />
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="font-heading font-bold text-navy text-lg">Customers</h2>
          <p className="text-sm text-gray">{customers.length} borrower{customers.length !== 1 ? 's' : ''}</p>
        </div>
        <div className="flex gap-2">
          <button className="btn-navy" onClick={openCreate}>
            <UserPlus size={14} style={{ marginRight: '4px' }} /> New Customer
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="panel" style={{ marginBottom: '12px' }}>
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center', padding: '4px 2px' }}>
          <div style={{ position: 'relative', flex: 1, minWidth: '220px' }}>
            <Search size={14} style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: 'var(--g3)' }} />
            <input
              className="form-input"
              style={{ paddingLeft: '32px' }}
              placeholder="Search by Client ID, name or phone…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          {branches.length > 1 && (
            <select className="form-input" style={{ width: 'auto', minWidth: '180px' }} value={branchFilter} onChange={(e) => setBranchFilter(e.target.value)}>
              <option value="">All branches</option>
              {branches.map((b) => (<option key={b.id} value={b.id}>{b.branchName}</option>))}
            </select>
          )}
        </div>
      </div>

      {loading ? (
        <div className="panel" style={{ padding: '24px', textAlign: 'center', color: 'var(--g3)' }}>Loading…</div>
      ) : customers.length === 0 ? (
        <div className="panel" style={{ padding: '32px', textAlign: 'center', color: 'var(--g3)' }}>
          No customers yet. Click <strong>New Customer</strong> to add one.
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {customers.map((c) => {
            const branch = branches.find((b) => b.id === c.branchId);
            return (
              <div key={c.id || c.clientId} className="agent-card fu">
                <div className="agent-card-bar active" />
                <div className="agent-card-body">
                  <div className="agent-card-top">
                    <div style={{ flex: 1 }}>
                      <div className="agent-name" style={{ cursor: 'pointer' }} onClick={() => navigate(`/owner/recovery/customers/${encodeURIComponent(c.clientId)}`)}>
                        {c.fullName}
                        {c.needsStatementUpload && (
                          <span title="Statement upload needed" style={{ marginLeft: 6, color: '#a16207', display: 'inline-flex', verticalAlign: 'middle' }}>
                            <AlertCircle size={13} />
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-gray">Client ID: <strong>{c.clientId}</strong></div>
                      <div className="text-xs text-gray" style={{ display: 'flex', alignItems: 'center', gap: '4px', marginTop: '2px' }}>
                        <Phone size={11} /> {c.primaryPhone}{c.alternatePhone ? ` · ${c.alternatePhone}` : ''}
                      </div>
                      {c.address && (
                        <div className="text-xs text-gray" style={{ display: 'flex', alignItems: 'flex-start', gap: '4px', marginTop: '2px' }}>
                          <MapPin size={11} style={{ marginTop: '2px', flexShrink: 0 }} /> {c.address}
                        </div>
                      )}
                      <div className="text-xs text-gray" style={{ marginTop: '4px' }}>Branch: {branch?.branchName || '—'}</div>
                    </div>
                  </div>
                </div>
                <div className="agent-card-actions">
                  <button className="agent-view-link" onClick={() => navigate(`/owner/recovery/customers/${encodeURIComponent(c.clientId)}`)}>
                    View profile
                  </button>
                  <button className="agent-view-link" onClick={() => openEdit(c)}>
                    <Edit2 size={12} style={{ marginRight: '4px' }} /> Edit
                  </button>
                  <button className="agent-view-link" onClick={() => navigate(`/owner/recovery/loans?clientId=${encodeURIComponent(c.clientId)}`)}>
                    View loans
                  </button>
                  <label className="agent-view-link" style={{ cursor: 'pointer' }} title="Upload CBS loan statement PDF for this customer">
                    <Upload size={12} style={{ marginRight: '4px' }} /> Statement
                    <input
                      type="file" accept=".pdf,application/pdf" style={{ display: 'none' }}
                      onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) uploadStatement(c, f); }}
                    />
                  </label>
                  <button className="agent-view-link text-red" onClick={() => removeCustomer(c)}>
                    <Trash2 size={12} style={{ marginRight: '4px' }} /> Delete
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showModal && (
        <div className="owner-modal-overlay open" onClick={(e) => { if (e.target === e.currentTarget) setShowModal(false); }}>
          <div className="owner-modal" style={{ width: '460px' }}>
            <div className="modal-head">
              <div className="modal-icon"><UserPlus size={16} /></div>
              <div>
                <div className="modal-title">{editing ? 'Edit Customer' : 'New Customer'}</div>
                <div className="modal-subtitle">Borrower record for recovery</div>
              </div>
              <button className="modal-close" onClick={() => setShowModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">Client ID</label>
                <input className="form-input" placeholder="e.g. CL-1042" value={form.clientId} onChange={(e) => setForm((p) => ({ ...p, clientId: e.target.value }))} disabled={!!editing} />
              </div>
              <div className="form-group">
                <label className="form-label">Full Name</label>
                <input className="form-input" value={form.fullName} onChange={(e) => setForm((p) => ({ ...p, fullName: e.target.value }))} />
              </div>
              <div className="form-group">
                <label className="form-label">Primary Phone</label>
                <input className="form-input" value={form.primaryPhone} onChange={(e) => setForm((p) => ({ ...p, primaryPhone: e.target.value }))} inputMode="tel" />
              </div>
              <div className="form-group">
                <label className="form-label">Alternate Phone (optional)</label>
                <input className="form-input" value={form.alternatePhone} onChange={(e) => setForm((p) => ({ ...p, alternatePhone: e.target.value }))} inputMode="tel" />
              </div>
              <div className="form-group">
                <label className="form-label">Address (optional)</label>
                <textarea className="form-input" rows={2} value={form.address} onChange={(e) => setForm((p) => ({ ...p, address: e.target.value }))} />
              </div>
              <div className="form-group">
                <label className="form-label">Branch</label>
                <select className="form-input" value={form.branchId} onChange={(e) => setForm((p) => ({ ...p, branchId: e.target.value }))}>
                  <option value="">Select branch</option>
                  {branches.filter((b) => b.isActive).map((b) => (
                    <option key={b.id} value={b.id}>{b.branchName} ({b.branchCode})</option>
                  ))}
                </select>
              </div>
              <div className="flex justify-end gap-2">
                <button className="btn-outline" onClick={() => setShowModal(false)}>Cancel</button>
                <button className="btn-navy" onClick={save}>{editing ? 'Save Changes' : 'Create Customer'}</button>
              </div>
            </div>
          </div>
        </div>
      )}

      <Toast msg={toast.msg} show={toast.show} />
    </OwnerLayout>
  );
}
